import { useEffect, useRef, useState, type ReactNode } from 'react';
import { Box, Button, Field, HStack, IconButton, Input, RadioGroup, Spinner, Stack, Text, useBreakpointValue } from '@chakra-ui/react';
import type { Bridge } from '@casa/bridge';
import type { Dashboard } from '@casa/model';
import {
    DashboardError,
    announceDashboard,
    followRename,
    keepNewDashboard,
    loadFromDevice,
    loadFromServer,
    loadFromUrl,
    readSource,
    SERVER_NAMES,
    suggestedName,
    type DashboardSource,
} from '../data/dashboard.ts';
import { findDeviceDashboard, isDashboardName, readDeviceDashboard, removeDeviceDashboard, writeDeviceDashboard } from '../data/device_store.ts';
import { availableStores, copyToStore, isServerStore, listStore, readStoreSettings, type StoreEntry, type StoreInfo, type StoreSettings, type WritableStore } from '../data/stores.ts';
import { dashboardSlug, tabDashboard } from '../data/tab_dashboard.ts';
import { routeState } from '../ui/route.ts';
import { IconGlyph } from '../ui/icon_glyph.tsx';
import { toaster } from '../ui/chakra.tsx';
import { ChromeDialog } from '../ui/chrome_dialog.tsx';
import { ChromeError } from '../ui/chrome_page.tsx';
import { ConfirmDialog } from '../ui/confirm_dialog.tsx';
import { DEMO_DASHBOARDS, DEMO_VERSIONS, demoName, type DemoVersion } from './demo_dashboard.ts';
import { NewDashboardForm } from './new_dashboard_form.tsx';
import { closeSettings } from './settings_navigation.ts';
import { SettingsGroup, SettingsRow } from './settings_ui.tsx';

// "Dashboards" (docs/specs/settings.md §4.3): the dashboards in the current store. Choosing one
// shows it and closes the settings (DB-2); choosing the one shown loads it again. New dashboards and
// imported files go into the current store (DB-3, DB-4), and so does the demo (demo-dashboard spec
// DD-6 to DD-9); one on this device can be renamed and deleted (DB-5); any can be copied to another
// store (DB-7).

function messageOf(error: unknown) : string {
    const issues = error instanceof DashboardError ? error.issues.map((issue) => `\n- ${issue}`).join('') : '';

    return `${error instanceof Error ? error.message : String(error)}${issues}`;
}

/** Loads a dashboard of the current store and makes it the one this device shows. */
async function load(shell: Bridge, settings: StoreSettings, entry: StoreEntry) : Promise<Dashboard> {
    switch (settings.current) {
        case 'device':
            return loadFromDevice(shell, entry.name);
        case 'casa':
        case 'cloud':
            return loadFromServer(shell, entry.name, settings.current);
        case 'url':
            return loadFromUrl(shell, { kind: 'url', url: settings.url?.url ?? entry.name, ...(settings.url?.authorization ? { authorization: settings.url.authorization } : {}) });
    }
}

function isShown(source: DashboardSource | undefined, settings: StoreSettings, entry: StoreEntry) : boolean {
    if (source?.kind !== settings.current) {
        return false;
    }

    return source.kind === 'url' ? source.url === settings.url?.url : source.name === entry.name;
}

/** A name, and for a copy the store it goes to. */
function NameDialog(props: {
    title: string;
    initial: string;
    stores?: StoreInfo[];
    action: string;
    onDone: (name: string, store: WritableStore) => Promise<string | undefined>;
    onClose: () => void;
}) : ReactNode {
    const [name, setName] = useState(props.initial);
    const first = props.stores?.[0]?.kind;
    const [store, setStore] = useState<WritableStore>(first !== undefined && isServerStore(first) ? first : 'device');
    const [problem, setProblem] = useState<string>();
    const [busy, setBusy] = useState(false);
    const isValid = isDashboardName(name.trim());

    const done = async () : Promise<void> => {
        setBusy(true);
        setProblem(await props.onDone(name.trim(), store));
        setBusy(false);
    };

    return (
        <ChromeDialog
            title={props.title}
            onClose={props.onClose}
            size="sm"
            footer={
                <>
                    <Button variant="outline" onClick={props.onClose}>
                        Cancel
                    </Button>
                    <Button colorPalette="blue" disabled={!isValid} loading={busy} onClick={() => void done()}>
                        {props.action}
                    </Button>
                </>
            }
        >
            <Stack gap="4">
                {props.stores && props.stores.length > 1 ? (
                    <RadioGroup.Root value={store} onValueChange={(details) => setStore(details.value === 'casa' || details.value === 'cloud' ? details.value : 'device')}>
                        <Stack gap="2">
                            {props.stores.map((entry) => (
                                <RadioGroup.Item key={entry.kind} value={entry.kind}>
                                    <RadioGroup.ItemHiddenInput />
                                    <RadioGroup.ItemIndicator />
                                    <RadioGroup.ItemText>
                                        {entry.title}
                                        {entry.detail ? ` (${entry.detail})` : ''}
                                    </RadioGroup.ItemText>
                                </RadioGroup.Item>
                            ))}
                        </Stack>
                    </RadioGroup.Root>
                ) : null}
                <Field.Root invalid={name.trim() !== '' && !isValid}>
                    <Field.Label>Name</Field.Label>
                    <Input aria-label="Name" autoFocus value={name} onChange={(event) => setName(event.target.value)} />
                    <Field.ErrorText>Letters, digits, spaces, ".", "_" and "-", at most 64.</Field.ErrorText>
                </Field.Root>
                {problem ? <ChromeError text={problem} /> : null}
            </Stack>
        </ChromeDialog>
    );
}

type Dialog =
    | { kind: 'new' }
    | { kind: 'demo' }
    | { kind: 'import'; text: string; suggested: string }
    | { kind: 'copy'; entry: StoreEntry }
    | { kind: 'rename'; entry: StoreEntry }
    | { kind: 'delete'; entry: StoreEntry };

/** `isFirstStart`: on the first start, the demo is the version that fits the window; in the settings, the user chooses (DD-7). */
export function DashboardsPart({ shell, onChanged, isFirstStart = false }: { shell: Bridge; onChanged?: () => void; isFirstStart?: boolean }) : ReactNode {
    const [settings, setSettings] = useState<StoreSettings>();
    const [stores, setStores] = useState<StoreInfo[]>([]);
    const [entries, setEntries] = useState<StoreEntry[]>();
    const [source, setSource] = useState<DashboardSource>();
    const [loading, setLoading] = useState<string>();
    const [error, setError] = useState<string>();
    const [dialog, setDialog] = useState<Dialog>();
    const [revision, setRevision] = useState(0);
    const fileInput = useRef<HTMLInputElement>(null);
    /** The dashboard whose actions (copy, rename, delete) are shown under it. */
    const [expanded, setExpanded] = useState<string>();
    // The phone's version where the app shows the phone's menu (dashboard_app.tsx), else the tablet's.
    const fitting: DemoVersion = useBreakpointValue({ base: true, md: false }, { ssr: false }) ? 'phone' : 'tablet';

    useEffect(() => {
        let isCurrent = true;
        void (async () => {
            setEntries(undefined);
            setError(undefined);
            const read = await readStoreSettings(shell);
            const all = await availableStores(shell);
            setSettings(read);
            setStores(all);
            setSource(await readSource(shell));
            try {
                const listed = await listStore(shell, read.current);
                if (isCurrent) {
                    setEntries(listed);
                }
            } catch (caught) {
                if (isCurrent) {
                    setEntries([]);
                    setError(messageOf(caught));
                }
            }
        })();

        return () => {
            isCurrent = false;
        };
    }, [shell, revision]);

    const refresh = () : void => {
        setRevision((value) => value + 1);
        onChanged?.();
    };

    /** Shows a dashboard on the screen behind, and closes the settings (DB-2). */
    const show = (dashboard: Dashboard) : void => {
        // A browser tab at /dash/<name> shows the dashboard its path names: another one is another path (settings §4.7).
        if (tabDashboard() !== undefined) {
            void readSource(shell).then((shown) => location.assign(shown?.kind === 'casa' ? `/dash/${dashboardSlug(shown.name)}` : '/dash'));

            return;
        }

        // Over the start page there is no dashboard to show it in: to the browser's own, which it is now.
        if (routeState().overHome === true) {
            location.assign('/dash');

            return;
        }

        announceDashboard(dashboard);
        closeSettings();
    };

    const choose = async (entry: StoreEntry) : Promise<void> => {
        if (!settings) {
            return;
        }

        setLoading(entry.name);
        setError(undefined);
        try {
            show(await load(shell, settings, entry));
        } catch (caught) {
            setError(messageOf(caught));
        } finally {
            setLoading(undefined);
        }
    };

    if (!settings) {
        return null;
    }

    const store = stores.find((entry) => entry.kind === settings.current);
    const writable = stores.filter((entry) => entry.isWritable);
    const currentKind = settings.current === 'url' ? undefined : settings.current;

    /** Keeps the demo's version in the current store as an import (DD-8), and shows it. */
    const tryDemo = async (version: DemoVersion) : Promise<void> => {
        if (!currentKind) {
            return;
        }

        setDialog(undefined);
        setLoading('demo');
        setError(undefined);
        try {
            show(await keepNewDashboard(shell, DEMO_DASHBOARDS[version], currentKind, demoName((entries ?? []).map((entry) => entry.name))));
        } catch (caught) {
            setError(messageOf(caught));
        } finally {
            setLoading(undefined);
        }
    };

    const importFile = async (file: File | undefined) : Promise<void> => {
        if (file) {
            setDialog({ kind: 'import', text: await file.text(), suggested: suggestedName(file.name, 'Imported') });
        }
    };

    return (
        <Stack gap="6">
            <SettingsGroup
                title={store && isServerStore(store.kind) && store.detail ? `${store.title} · ${store.detail}` : store?.title}
                note={settings.current === 'url' ? 'A web address can only be read: choose it to load it again. Editing it asks where to keep your version.' : undefined}
            >
                {entries === undefined ? <Spinner m="4" /> : null}
                {entries?.length === 0 && !error ? <SettingsRow title="No dashboards here yet" detail="Make one below, or import a file." /> : null}
                {entries?.map((entry) => (
                    <Box key={entry.name} data-testid={`dashboard-${entry.name}`}>
                        <SettingsRow
                            icon="table-cells-large"
                            title={entry.name}
                            detail={isShown(source, settings, entry) ? 'Shown now · choose it to load it again' : entry.updated ? `Saved ${new Date(entry.updated).toLocaleString()}` : undefined}
                            isCurrent={isShown(source, settings, entry)}
                            onClick={() => void choose(entry)}
                            end={
                                loading === entry.name ? (
                                    <Spinner size="sm" />
                                ) : (
                                    <IconButton
                                        aria-label={`More for ${entry.name}`}
                                        aria-expanded={expanded === entry.name}
                                        variant="ghost"
                                        size="sm"
                                        onClick={() => setExpanded(expanded === entry.name ? undefined : entry.name)}
                                    >
                                        <IconGlyph name="ellipsis" size={16} color="#a1a1aa" />
                                    </IconButton>
                                )
                            }
                        />
                        {expanded === entry.name ? (
                            <HStack gap="2" px="4" pb="3" ps="14" flexWrap="wrap">
                                <Button size="xs" variant="outline" onClick={() => setDialog({ kind: 'copy', entry })}>
                                    Copy to…
                                </Button>
                                {settings.current === 'device' ? (
                                    <Button size="xs" variant="outline" onClick={() => setDialog({ kind: 'rename', entry })}>
                                        Rename
                                    </Button>
                                ) : null}
                                {settings.current === 'device' ? (
                                    <Button size="xs" variant="outline" colorPalette="red" onClick={() => setDialog({ kind: 'delete', entry })}>
                                        Delete
                                    </Button>
                                ) : null}
                            </HStack>
                        ) : null}
                    </Box>
                ))}
            </SettingsGroup>
            {error ? <ChromeError text={error} /> : null}
            {currentKind ? (
                <SettingsGroup>
                    <SettingsRow icon="plus" title="New dashboard" onClick={() => setDialog({ kind: 'new' })} data-testid="new-dashboard" />
                    <SettingsRow icon="file-import" title="Import a file" detail="A dashboard file from this device" onClick={() => fileInput.current?.click()} />
                    <SettingsRow
                        icon="house"
                        title="Try the demo"
                        detail="A simulated home, no devices needed"
                        onClick={() => (isFirstStart ? void tryDemo(fitting) : setDialog({ kind: 'demo' }))}
                        end={loading === 'demo' ? <Spinner size="sm" /> : undefined}
                    />
                </SettingsGroup>
            ) : null}
            {isServerStore(settings.current) ? (
                <Text fontSize="sm" color="fg.muted" px="3">
                    Dashboards on {SERVER_NAMES[settings.current]} are renamed and deleted on its admin page, in a browser.
                </Text>
            ) : null}
            <input
                ref={fileInput}
                type="file"
                accept=".json,application/json"
                hidden
                data-testid="import-file"
                onChange={(event) => {
                    void importFile(event.target.files?.[0]);
                    event.target.value = '';
                }}
            />

            {dialog?.kind === 'new' && currentKind ? (
                <ChromeDialog title="New dashboard" onClose={() => setDialog(undefined)} focusesField={false}>
                    <NewDashboardForm
                        shell={shell}
                        store={currentKind}
                        onCreated={(dashboard) => {
                            setDialog(undefined);
                            show(dashboard);
                        }}
                    />
                </ChromeDialog>
            ) : null}
            {dialog?.kind === 'demo' ? (
                <ChromeDialog title="Try the demo" onClose={() => setDialog(undefined)} focusesField={false}>
                    <SettingsGroup note="A copy is kept with your dashboards. Its devices are made up: nothing in your home is touched.">
                        {([fitting, fitting === 'phone' ? 'tablet' : 'phone'] as const).map((version) => (
                            <SettingsRow key={version} title={DEMO_VERSIONS[version].title} detail={DEMO_VERSIONS[version].detail} onClick={() => void tryDemo(version)} />
                        ))}
                    </SettingsGroup>
                </ChromeDialog>
            ) : null}
            {dialog?.kind === 'import' && currentKind ? (
                <NameDialog
                    title="Import a file"
                    initial={dialog.suggested}
                    action="Import"
                    onClose={() => setDialog(undefined)}
                    onDone={async (name) => {
                        try {
                            const dashboard = await keepNewDashboard(shell, dialog.text, currentKind, name);
                            setDialog(undefined);
                            show(dashboard);

                            return undefined;
                        } catch (caught) {
                            return messageOf(caught);
                        }
                    }}
                />
            ) : null}
            {dialog?.kind === 'copy' ? (
                <NameDialog
                    title={`Copy "${dialog.entry.name}"`}
                    initial={settings.current === 'url' ? suggestedName(new URL(dialog.entry.name).pathname) : dialog.entry.name}
                    stores={writable}
                    action="Copy"
                    onClose={() => setDialog(undefined)}
                    onDone={async (name, to) => {
                        try {
                            const copied = await copyToStore(shell, { kind: settings.current, name: dialog.entry.name }, { kind: to, name });
                            if (!copied) {
                                return `There is a dashboard called "${name}" there already: choose another name.`;
                            }

                            setDialog(undefined);
                            toaster.create({ description: `Copied to "${name}" ${to === 'device' ? 'on this device' : `on ${SERVER_NAMES[to]}`}.`, type: 'success' });
                            refresh();

                            return undefined;
                        } catch (caught) {
                            return messageOf(caught);
                        }
                    }}
                />
            ) : null}
            {dialog?.kind === 'rename' ? (
                <NameDialog
                    title={`Rename "${dialog.entry.name}"`}
                    initial={dialog.entry.name}
                    action="Rename"
                    onClose={() => setDialog(undefined)}
                    onDone={async (name) => {
                        const from = dialog.entry.name;
                        const taken = await findDeviceDashboard(shell, name);
                        if (taken !== undefined && taken.toLowerCase() !== from.toLowerCase()) {
                            return `There is a dashboard called "${taken}" on this device already.`;
                        }

                        const text = await readDeviceDashboard(shell, from);
                        if (text === undefined) {
                            return `"${from}" is not on this device any more.`;
                        }

                        await removeDeviceDashboard(shell, from);
                        await writeDeviceDashboard(shell, name, text);
                        await followRename(shell, 'device', from, name);
                        setDialog(undefined);
                        refresh();

                        return undefined;
                    }}
                />
            ) : null}
            {dialog?.kind === 'delete' ? (
                <ConfirmDialog
                    title={`Delete "${dialog.entry.name}"?`}
                    message={
                        isShown(source, settings, dialog.entry)
                            ? 'It is shown now, and stays on the screen until you choose another; saving it in edit mode keeps it again. There is no undo.'
                            : 'It is removed from this device. There is no undo.'
                    }
                    confirm="Delete"
                    cancel="Keep it"
                    onConfirm={() => {
                        const { name } = dialog.entry;
                        setDialog(undefined);
                        void removeDeviceDashboard(shell, name).then(refresh);
                    }}
                    onCancel={() => setDialog(undefined)}
                />
            ) : null}
        </Stack>
    );
}
